import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import Map, {
    Layer,
    LngLatBoundsLike,
    Marker,
    Popup,
    Source
} from 'react-map-gl/mapbox';
import type { MapRef } from 'react-map-gl/mapbox';
import {
    CONTROL_ZINDEX,
    POPUP_ZINDEX,
    RegionBoundingBox,
    getLocaleRegionBoundingBox,
    increaseBoundsBy,
    locationsToBounds
} from '../util/map-helpers';
import styles from './task-detail-map.module.scss';
import { MapUserLocation, UserMapMarker } from './user-map-marker';
import { TaskLocation } from '@/api';
import { MapButton } from './map-button';
import { UILoadingState, UISwitch, UIText } from '@/ui';
import { uuidv4 } from '@/common/util';
import { useFormatDate } from '@/common/hooks';
import { blue, mint } from '@/ui/styles';
import { BeaconMapMarker, BeaconMapMarkerProps } from './beacon-map-marker';
import { TaskInfoItem } from './task-info-item';
import { MAPBOX_TOKEN } from '@/config';
import { useRegion } from '@/common/providers/region-provider';
import { useTasksTranslation } from '../hooks';

// const radius = 50;
// const maxZoom = 20;
// const minZoom = 1;
// const minPoints = 2;
// const extent = 512;
// const nodeSize = 64;

type TaskDetailMapProps = {
    taskId: string;
    taskStatus: 'open' | 'closed';
    route: TaskLocation[];
    userImage: string;
    user: MapUserLocation | null;
    beacons?: BeaconMapMarkerProps[];
    showControls?: boolean;
    shouldAnimate?: boolean;
    status: 'error' | 'success' | 'pending';
};
export function TaskDetailMap(props: TaskDetailMapProps): ReactElement {
    const {
        taskId,
        taskStatus,
        user,
        beacons = [],
        userImage,
        route,
        showControls = true,
        shouldAnimate = true,
        status
    } = props;

    const { t } = useTasksTranslation();
    const { resolvedRegion } = useRegion();
    const { formatDateTime } = useFormatDate();
    const userRef = useRef<MapUserLocation | null>(user);
    useEffect(() => {
        userRef.current = user;
    });

    const mapRef = useRef<MapRef>(null);
    const [mapLoading, setMapLoading] = useState(true);
    const [focused, setFocused] = useState(true);
    const [showRoute, setShowRoute] = useState(taskStatus === 'closed');
    const [showTimeline, setShowTimeline] = useState(false);
    const [showBeacons, setShowBeacons] = useState(true);
    const [sliderValue, setSliderValue] = useState(0);
    const [popupInfo, setPopupInfo] = useState<MapPopupProps | null>(null);

    // Switch back to defaults when task changes
    useEffect(() => {
        setShowRoute(taskStatus === 'closed');
        setShowTimeline(false);
        setShowBeacons(true);
        setSliderValue(0);
        setFocused(true);
    }, [taskId, taskStatus]);

    const hasLocationData = useMemo(() => {
        return route.length > 0 || !!user || beacons.length > 0;
    }, [route, user, beacons]);

    const initialRegion: LngLatBoundsLike = useMemo(() => {
        const bounds = getBoundsToFit({
            user: userRef.current,
            route,
            beacons,
            sliderValue,
            showRoute,
            showTimeline,
            showBeacons,
            region: resolvedRegion
        });

        return [bounds.sw.lon, bounds.sw.lat, bounds.ne.lon, bounds.ne.lat];

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const focusMap = () => {
        if (!hasLocationData) {
            return;
        }

        const bounds = getBoundsToFit({
            user: userRef.current,
            route,
            beacons,
            sliderValue,
            showRoute,
            showTimeline,
            showBeacons,
            region: resolvedRegion
        });

        const maxZoom = 17;

        mapRef.current?.fitBounds(
            [bounds.sw.lon, bounds.sw.lat, bounds.ne.lon, bounds.ne.lat],
            { maxZoom, animate: shouldAnimate }
        );
    };

    const focusMapRef = useRef(focusMap);
    useEffect(() => {
        focusMapRef.current = focusMap;
    });

    // Focus on user
    useEffect(() => {
        if (mapLoading || !focused || !user) {
            return;
        }
        focusMapRef.current();
    }, [mapLoading, focused, user, status]);

    // Detect when the task changes
    useEffect(() => {
        setFocused(true);
        focusMapRef.current();
    }, [taskId]);

    useEffect(() => {
        // Turn off beacon popups when beacons are turned off
        if (!showBeacons && popupInfo?.beacon) {
            setPopupInfo(null);
        }
    }, [showBeacons, showRoute, showTimeline, popupInfo]);

    useEffect(() => {
        if (focused) {
            focusMapRef.current();
        }
    }, [showRoute, focused, route, user]);

    const routeCoordinates = useMemo(
        () => route.map((location) => [location.lng, location.lat]),
        [route]
    );

    const routeSourceId = useMemo(() => {
        return uuidv4();
    }, []);

    const showBeaconsToggle = useMemo(() => {
        return beacons.length > 0 && showControls;
    }, [beacons, showControls]);

    const showRouteToggle = useMemo(() => {
        return route.length > 0 && showControls;
    }, [route, showControls]);

    const showTimelineToggle = useMemo(() => {
        return route.length > 0 && showControls;
    }, [route, showControls]);

    useEffect(() => {
        const delays = [100, 200, 300]; // ms
        const timers = delays.map((delay) =>
            setTimeout(() => {
                if (mapRef.current) {
                    mapRef.current.resize();
                }
            }, delay)
        );

        return () => timers.forEach(clearTimeout);
    }, []);

    return (
        <div className={styles.container}>
            <Map
                ref={mapRef}
                initialViewState={{
                    bounds: initialRegion,
                    fitBoundsOptions: {
                        maxZoom: 17
                    }
                }}
                style={{ width: '100%', height: '100%' }}
                mapStyle="mapbox://styles/mapbox/streets-v12"
                mapboxAccessToken={MAPBOX_TOKEN}
                onLoad={() => {
                    setMapLoading(false);
                }}
                onDragStart={() => setFocused(false)}
                onTouchMove={() => setFocused(false)}
                onZoomStart={(e) => {
                    if (
                        (e as unknown as { originalEvent: boolean })
                            .originalEvent
                    ) {
                        setFocused(false);
                    }
                }}
            >
                {taskStatus === 'closed' && route.length > 1 && (
                    <EventMapMarker
                        lat={route[0].lat}
                        lng={route[0].lng}
                        color={mint}
                    />
                )}
                {(taskStatus === 'closed' || showRoute) && route.length > 0 && (
                    <EventMapMarker
                        lat={route[route.length - 1].lat}
                        lng={route[route.length - 1].lng}
                        color={blue}
                    />
                )}
                {showBeacons &&
                    beacons.map((beacon, index) => (
                        <BeaconMapMarker
                            key={index}
                            {...beacon}
                            opacity={showTimeline ? 0.5 : 1}
                            isSelected={
                                popupInfo?.beacon?.beacon.mac ===
                                beacon.beacon.mac
                            }
                            onClick={() => {
                                setPopupInfo({
                                    lat: beacon.lat,
                                    lng: beacon.lng,
                                    beacon: beacon
                                });
                            }}
                        />
                    ))}
                {user && taskStatus === 'open' && (
                    <UserMapMarker
                        lat={user.lat}
                        lng={user.lng}
                        users={[user]}
                        opacity={showTimeline ? 0.5 : 1}
                        // onPress={() =>
                        //     onMarkerPress([marker.properties.user])
                        // }
                    />
                )}
                {showTimeline && route[sliderValue] && (
                    <UserMapMarker
                        lat={route[sliderValue].lat}
                        lng={route[sliderValue].lng}
                        users={[
                            {
                                userId: '1',
                                taskId: taskId,
                                image: userImage,
                                lat: route[sliderValue].lat,
                                lng: route[sliderValue].lng,
                                acc: route[sliderValue].acc
                            }
                        ]}
                    />
                )}
                {showRoute && (
                    <Source
                        id={`routeSource-${routeSourceId}`}
                        type="geojson"
                        data={{
                            type: 'Feature',
                            properties: {},
                            geometry: {
                                type: 'LineString',
                                coordinates: routeCoordinates
                            }
                        }}
                    >
                        <Layer
                            id={`routeLayer-${routeSourceId}`}
                            type="line"
                            layout={{
                                'line-join': 'round',
                                'line-cap': 'round'
                            }}
                            paint={{
                                'line-color': '#25b1be',
                                'line-width': 3
                            }}
                        />
                    </Source>
                )}
                {popupInfo && (
                    <MapPopup
                        {...popupInfo}
                        onClose={() => {
                            setPopupInfo(null);
                        }}
                    />
                )}
            </Map>
            {showTimeline && (
                <div
                    className={styles.timelineContainer}
                    style={{ zIndex: CONTROL_ZINDEX }}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between'
                        }}
                    >
                        {route[sliderValue] && (
                            <UIText>
                                {formatDateTime(
                                    new Date(route[sliderValue].ts)
                                )}
                            </UIText>
                        )}
                    </div>

                    <input
                        type="range"
                        aria-label={t(
                            'components.task_detail_map.timeline_slider_label'
                        )}
                        min="0"
                        max={route.length - 1}
                        value={sliderValue}
                        onChange={(e) => setSliderValue(Number(e.target.value))}
                        style={{ width: '100%' }}
                    />
                </div>
            )}
            {(showBeaconsToggle || showRouteToggle || showTimelineToggle) && (
                <div
                    style={{
                        position: 'absolute',
                        right: 16,
                        top: 16,
                        zIndex: CONTROL_ZINDEX
                    }}
                >
                    <div className={styles.buttonGroupContainer}>
                        {showBeaconsToggle && (
                            <label className={styles.switchButtonContainer}>
                                <UIText>
                                    {t(
                                        'components.task_detail_map.toggle_beacons'
                                    )}
                                </UIText>
                                <UISwitch
                                    checked={showBeacons}
                                    onChange={(value) => {
                                        setShowBeacons(value);
                                    }}
                                    small
                                />
                            </label>
                        )}
                        {showTimelineToggle && (
                            <>
                                <div className={styles.divider} />
                                <label className={styles.switchButtonContainer}>
                                    <UIText>
                                        {t(
                                            'components.task_detail_map.toggle_timeline'
                                        )}
                                    </UIText>
                                    <UISwitch
                                        checked={showTimeline}
                                        onChange={setShowTimeline}
                                        small
                                    />
                                </label>
                            </>
                        )}
                        {showRouteToggle && (
                            <>
                                <div className={styles.divider} />
                                <label className={styles.switchButtonContainer}>
                                    <UIText>
                                        {t(
                                            'components.task_detail_map.toggle_route'
                                        )}
                                    </UIText>
                                    <UISwitch
                                        checked={showRoute}
                                        onChange={setShowRoute}
                                        small
                                    />
                                </label>
                            </>
                        )}
                    </div>
                </div>
            )}

            {hasLocationData && (
                <div
                    style={{
                        position: 'absolute',
                        right: 16,
                        bottom: showTimeline ? 24 + 86 : 24,
                        zIndex: CONTROL_ZINDEX
                    }}
                >
                    <MapButton
                        onClick={() => {
                            setFocused((focused) => !focused);
                        }}
                        selected={focused}
                    />
                </div>
            )}

            <MapStatus status={status} hasLocationData={hasLocationData} />
        </div>
    );
}

function MapStatus(props: {
    status: 'error' | 'success' | 'pending';
    hasLocationData: boolean;
}): ReactElement {
    const { status, hasLocationData } = props;
    const { t } = useTasksTranslation();

    const showStatus = useMemo(() => {
        return !(status === 'success' && hasLocationData);
    }, [status, hasLocationData]);

    if (!showStatus) {
        return <></>;
    }

    return (
        <div
            style={{
                position: 'absolute',
                left: 0,
                top: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: CONTROL_ZINDEX,
                pointerEvents: 'none'
            }}
        >
            <div
                style={{
                    backgroundColor: 'white',
                    padding: 16,
                    borderRadius: 8
                }}
            >
                {status === 'pending' && <UILoadingState />}
                {status === 'error' && (
                    <UIText style={{ textAlign: 'center' }}>
                        {t('components.task_detail_map.error')}
                    </UIText>
                )}
                {status === 'success' && !hasLocationData && (
                    <UIText style={{ textAlign: 'center' }}>
                        {t('components.task_detail_map.no_location_data')}
                    </UIText>
                )}
            </div>
        </div>
    );
}

function getBoundsToFit(data: {
    user: MapUserLocation | null;
    route: TaskLocation[];
    beacons: BeaconMapMarkerProps[];
    sliderValue: number;
    showRoute: boolean;
    showTimeline: boolean;
    showBeacons: boolean;
    region: 'eu' | 'us';
}): RegionBoundingBox {
    const {
        user,
        route,
        beacons,
        sliderValue,
        showRoute,
        showTimeline,
        showBeacons,
        region
    } = data;

    const userLocation = user ? [user] : [];
    const routeLocations: { lat: number; lng: number }[] = showRoute
        ? [...route]
        : [];
    const beaconLocations: { lat: number; lng: number }[] = showBeacons
        ? beacons
        : [];

    let coordsToFit: { lat: number; lng: number }[] = [];

    if (showTimeline && route[sliderValue]) {
        coordsToFit = [route[sliderValue]];
    } else {
        coordsToFit = [...userLocation, ...routeLocations, ...beaconLocations];
    }

    let bounds: RegionBoundingBox;
    if (coordsToFit.length === 0) {
        bounds = getLocaleRegionBoundingBox(region);
    } else {
        bounds = locationsToBounds(coordsToFit);
    }

    return increaseBoundsBy(bounds, 25);
}

const EVENT_MARKER_SIZE = 20;

type EventMapMarkerProps = {
    lat: number;
    lng: number;
    color?: string;
};

function EventMapMarker(props: EventMapMarkerProps): ReactElement {
    const { lat, lng, color } = props;

    return (
        <Marker longitude={lng} latitude={lat}>
            <div
                style={{
                    width: EVENT_MARKER_SIZE,
                    height: EVENT_MARKER_SIZE,
                    backgroundColor: 'white',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '50%'
                }}
            >
                <div
                    style={{
                        width: EVENT_MARKER_SIZE - 4,
                        height: EVENT_MARKER_SIZE - 4,
                        backgroundColor: color,
                        borderRadius: '50%'
                    }}
                />
            </div>
        </Marker>
    );
}

type MapPopupProps = {
    lat: number;
    lng: number;
    anchor?:
        | 'top'
        | 'bottom'
        | 'center'
        | 'left'
        | 'right'
        | 'top-left'
        | 'top-right'
        | 'bottom-left'
        | 'bottom-right';
    onClose?: () => void;
    beacon?: BeaconMapMarkerProps | null;
};

function MapPopup(props: MapPopupProps): ReactElement {
    const { lat, lng, anchor = 'top', onClose, beacon = null } = props;
    const { t } = useTasksTranslation();
    const { formatSmartShortTimeAndDate } = useFormatDate();

    return (
        <Popup
            anchor={anchor}
            longitude={lng}
            latitude={lat}
            onClose={onClose}
            closeButton={false}
            style={{ zIndex: POPUP_ZINDEX }}
            className={styles.popup}
        >
            <div className={styles.contentContainer}>
                {beacon && (
                    <>
                        <TaskInfoItem
                            title={t('shared.tasks.beacon')}
                            rightTitle={formatSmartShortTimeAndDate(
                                beacon.updatedAt
                            )}
                            text={t('shared.tasks.beacon_name_with_distance', {
                                name: beacon.beacon.name,
                                distance: beacon.beacon.distanceMeters
                            })}
                        />

                        <TaskInfoItem
                            title={t('components.map_popup.tags_title')}
                            tags={beacon.beacon.tags.map((tag) => ({
                                text: tag
                            }))}
                        />
                    </>
                )}
            </div>
        </Popup>
    );
}
